import type { Environment } from '@infinex/chain-config';

import type { SessionKeyOptions } from '../api/auth';
import { configureWebSharedApi, type WebSharedApiConfig } from '../api/config';
import { configurePerpsAppUrl } from '../lib/environment';

interface WebSharedConfig extends WebSharedApiConfig {
  environment: Environment;
  workerMainUrl: string;
  // Asset action base URLs
  assetActionsBaseUrls?: {
    swidge: string;
    move: string;
    sendWallet: string;
    sendVault: string;
  };
  // Perps app URL for navigation
  perpsAppUrl?: string;
  // The _default_ session key user type to use for requiring session keys
  sessionKeyUserType?: SessionKeyOptions['sessionKeyUserType'];
}

let config: WebSharedConfig | null = null;

/**
 * Configure the web-shared package with required environment and optional API dependencies.
 * This must be called before using any web-shared functionality.
 *
 * @example
 * ```ts
 * // Create your client (if using API features)
 * const client = createClient({
 *   url: envVars.WORKER_MAIN_URL,
 *   wsUrl: envVars.WORKER_MAIN_WS_URL,
 *   rpId: envVars.WEBAUTHN_RPID,
 * });
 *
 * // Configure the web-shared package
 * configureWebShared({
 *   environment: 'prod',
 *   assetActionsBaseUrls: {
 *     swidge: '/swidge',
 *     move: '/move',
 *     sendWallet: '/portfolio/wallet/send',
 *     sendVault: '/portfolio/vault/send',
 *   },
 *   perpsAppUrl: 'https://perps.app.infinex.xyz',
 *   api: {
 *     client,
 *     createQueryHook,
 *     createMutationHook,
 *   },
 *   apiV2: trpc,
 *   trpcClientV2: trpcClient,
 *   sessionKeyUserType: 'operation',
 * });
 * ```
 */
export function configureWebShared(newConfig: WebSharedConfig): void {
  config = newConfig;

  // If API configuration is provided, configure the API module
  if (
    newConfig.api ||
    newConfig.apiV2 ||
    newConfig.trpcClientV2 ||
    newConfig.workerMainUrl
  ) {
    configureWebSharedApi({
      ...(newConfig.api && { api: newConfig.api }),
      ...(newConfig.apiV2 && { apiV2: newConfig.apiV2 }),
      ...(newConfig.trpcClientV2 && { trpcClientV2: newConfig.trpcClientV2 }),
      ...(newConfig.workerMainUrl && {
        workerMainUrl: newConfig.workerMainUrl,
      }),
    });
  }

  // Configure Perps app URL if provided
  if (newConfig.perpsAppUrl) {
    configurePerpsAppUrl(newConfig.perpsAppUrl);
  }
}

/**
 * Get the configured environment settings.
 * Throws if configureWebShared has not been called.
 */
export function getEnvironment(): WebSharedConfig['environment'] {
  if (!config) {
    throw new Error(
      'Web Shared not configured. Please call configureWebShared() before using web-shared features.'
    );
  }
  return config.environment;
}

/**
 * Get the configured asset action base URLs.
 * Throws if configureWebShared has not been called.
 */
export function getAssetActionsBaseUrls(): WebSharedConfig['assetActionsBaseUrls'] {
  if (!config) {
    throw new Error(
      'Web Shared not configured. Please call configureWebShared() before using web-shared features.'
    );
  }
  return config.assetActionsBaseUrls;
}

/**
 * Get the configured defualt session key user type.
 * Throws if configureWebShared has not been called or sessionKeyUserType not configured.
 */
export function getSessionKeyUserType(): SessionKeyOptions['sessionKeyUserType'] {
  if (!config) {
    throw new Error(
      'Web Shared not configured. Please call configureWebShared() before using web-shared features.'
    );
  }
  return config.sessionKeyUserType;
}
